import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// From Caleb ♡ — a private drawer of the things he's given you.
// The site only knows WHEN something unlocks (study-hour milestones). What the
// gift is comes from him; you add it here once you've received it.

/* ── icons ── */
const Svg = ({ children, size = 16, color = '#c9935a', sw = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const PlusIcon = () => <Svg><path d="M12 5v14" /><path d="M5 12h14" /></Svg>;
const CloseIcon = () => <Svg><path d="M6 6l12 12" /><path d="M18 6L6 18" /></Svg>;
const PencilIcon = () => <Svg size={14}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></Svg>;
const EnvelopeIcon = ({ size = 18 }) => <Svg size={size}><rect x="3" y="5.5" width="18" height="13" rx="2" /><path d="m3.5 7 8.5 6 8.5-6" /></Svg>;
const ImageIcon = ({ size = 18 }) => <Svg size={size}><rect x="3" y="3.5" width="18" height="17" rx="2" /><circle cx="8.5" cy="9" r="1.6" /><path d="m21 16-5-5-9 9.5" /></Svg>;
const HeartIcon = ({ size = 13 }) => (
  <Svg size={size}><path d="M12 20.5s-7.5-4.6-9.2-9.3C1.6 7.8 3.8 4.5 7.2 4.5c2 0 3.5 1.1 4.8 2.8 1.3-1.7 2.8-2.8 4.8-2.8 3.4 0 5.6 3.3 4.4 6.7-1.7 4.7-9.2 9.3-9.2 9.3z" /></Svg>
);

const longDate = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
};
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EMPTY_FORM = { title: '', content: '', date: '', milestone: '', photos: [], allRotation: false };

// each sticker gets its own little tilt, the same every time (like it was stuck on by hand)
const tiltOf = (id) => { let h = 0; String(id).split('').forEach(c => { h = (h * 31 + c.charCodeAt(0)) | 0; }); return ((Math.abs(h) % 9) - 4) * 0.9; };
// did he already sign it?
const signed = (text) => /caleb|— ?c\b|♡ ?c$/i.test(((text || '').trim().split('\n').pop() || ''));

const newGroupId = () => `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

// a gift = everything that came together: his note and the stickers he made
const groupGifts = (items) => {
  const gifts = [];
  const byGroup = {};
  items.forEach(k => {
    const key = k.group_id || k.id;
    if (!byGroup[key]) { byGroup[key] = { key, group_id: k.group_id || null, items: [] }; gifts.push(byGroup[key]); }
    byGroup[key].items.push(k);
  });
  return gifts.map(g => {
    const note = g.items.find(k => k.kind === 'note') || null;
    const images = g.items.filter(k => k.kind === 'image').sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    const first = note || images[0];
    return { ...g, note, images, title: g.items.map(k => k.title).find(Boolean) || '', received_date: first.received_date,
      milestone_hours: first.milestone_hours, milestone_subject: first.milestone_subject };
  });
};

export default function Keepsakes() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [items, setItems] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [waiting, setWaiting] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openKey, setOpenKey] = useState(null);      // the gift being edited
  const [viewer, setViewer] = useState(null);        // { key, i }: the sticker being looked at
  const [expanded, setExpanded] = useState({});      // long notes unfolded
  const [shown, setShown] = useState({});            // gifts that have scrolled into view (their stickers stick on)
  const observer = useRef(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmPhoto, setConfirmPhoto] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showMilestones, setShowMilestones] = useState(false);
  const [milestoneDraft, setMilestoneDraft] = useState('');
  const fileRef = useRef(null);
  const moreRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceTarget = useRef(null);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    load();
    // opening the drawer quiets the "something is waiting" notice
    axios.post(`${API}/study/gifts/seen`, { token }).catch(() => {});
  }, []);

  const describeError = (err) => {
    const d = err?.response?.data?.detail;
    return typeof d === 'string' ? d : (err?.message || 'something went wrong');
  };

  const load = () => {
    return axios.get(`${API}/study/keepsakes?token=${token}`)
      .then(res => {
        setItems(res.data.keepsakes || []);
        setMilestones(res.data.milestones || []);
        setWaiting(res.data.waiting || []);
        setLoaded(true);
        return res.data;
      })
      .catch(err => { setLoaded(true); setErrorMsg(`couldn't open the drawer: ${describeError(err)}`); });
  };

  const gifts = groupGifts(items);
  const open = openKey ? gifts.find(g => g.key === openKey) || null : null;

  // each subject has its own milestones: "10 hours of nursing", "10 hours of software engineering"
  const subjectNames = {};
  milestones.forEach(m => { subjectNames[m.subject] = m.subject_name; });
  const manySubjects = Object.keys(subjectNames).length > 1;
  const milestoneLabel = (h, subject = 'nursing') =>
    (manySubjects ? `${h} hours of ${subjectNames[subject] || subject}` : `${h} hours together`);
  const msValue = (subject, h) => `${subject || 'nursing'}:${h}`;
  const msParse = (v) => {
    if (!v) return { hours: null, subject: null };
    const [subject, h] = String(v).includes(':') ? String(v).split(':') : ['nursing', v];
    return { hours: Number(h), subject };
  };

  // send several images, one after another, into a gift
  const uploadImages = (photos, base) => photos.reduce((p, ph, i) => p.then(() => {
    setProgress(`keeping it… ${i + 1} of ${photos.length}`);
    const data = new FormData();
    data.append('file', ph.file);
    const q = new URLSearchParams({
      token, title: base.title || '', received_date: base.date || '', group_id: base.groupId,
      milestone_hours: String(base.milestone || 0), milestone_subject: base.milestoneSubject || 'nursing',
      in_rotation: ph.inRotation ? 'true' : 'false',
    });
    return axios.post(`${API}/study/keepsakes/image?${q.toString()}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
  }), Promise.resolve());

  // ---- adding ----
  const startAdd = (preset = {}) => {
    setForm({ ...EMPTY_FORM, date: todayYmd(), milestone: waiting[0] ? msValue(waiting[0].subject, waiting[0].hours) : '', ...preset, photos: [] });
    setErrorMsg('');
    setAdding(true);
  };

  const pickFiles = (files) => {
    const list = Array.from(files || []).filter(f => f.type.startsWith('image/') || /\.(png|jpe?g|webp|gif|heic)$/i.test(f.name));
    if (!list.length) return;
    setForm(f => ({ ...f, photos: [...(f.photos || []), ...list.map(file => ({ file, preview: URL.createObjectURL(file), inRotation: !!f.allRotation }))] }));
  };
  const setAllRotation = (on) => setForm(f => ({ ...f, allRotation: on, photos: (f.photos || []).map(p => ({ ...p, inRotation: on })) }));

  const saveNew = () => {
    setBusy(true);
    setErrorMsg('');
    const { hours: milestone, subject: milestoneSubject } = msParse(form.milestone);
    const photos = form.photos || [];
    const groupId = photos.length + (form.content.trim() ? 1 : 0) > 1 ? newGroupId() : '';
    const base = { title: form.title, date: form.date, milestone, milestoneSubject, groupId };
    const note = form.content.trim()
      ? axios.post(`${API}/study/keepsakes/note`, {
        token, content: form.content, title: form.title, received_date: form.date, milestone_hours: milestone,
        milestone_subject: milestoneSubject, group_id: groupId || null,
      })
      : Promise.resolve();
    note
      .then(() => uploadImages(photos, base))
      .then(() => { setAdding(false); return load(); })
      .catch(err => { setErrorMsg(describeError(err)); load(); })
      .finally(() => { setBusy(false); setProgress(''); });
  };

  const canSave = form.content.trim().length > 0 || (form.photos || []).length > 0;

  // ---- editing ----
  const closeGift = () => { setOpenKey(null); setEditing(false); setEdit(null); setConfirmDelete(false); setConfirmPhoto(''); };

  const startEdit = (g) => {
    setOpenKey(g.key);
    setConfirmDelete(false); setConfirmPhoto(''); setErrorMsg('');
    setEdit({
      title: g.title || '', content: g.note ? g.note.content || '' : '', date: g.received_date || todayYmd(),
      milestone: g.milestone_hours ? msValue(g.milestone_subject, g.milestone_hours) : '',
    });
    setEditing(true);
  };

  // make sure a gift has a group id before more pieces join it
  const ensureGroup = (g) => {
    if (g.group_id) return Promise.resolve(g.group_id);
    const gid = newGroupId();
    return Promise.all(g.items.map(k => axios.put(`${API}/study/keepsakes/${k.id}`, { token, group_id: gid }))).then(() => gid);
  };

  const saveEdit = () => {
    setBusy(true);
    setErrorMsg('');
    const { hours, subject } = msParse(edit.milestone);
    const common = { token, title: edit.title, received_date: edit.date, milestone_hours: edit.milestone ? hours : 0, milestone_subject: edit.milestone ? subject : null };
    const g = open;
    const jobs = g.items.map(k => axios.put(`${API}/study/keepsakes/${k.id}`, { ...common, ...(k.kind === 'note' ? { content: edit.content.trim() || k.content } : {}) }));
    // a note written into a gift that didn't have one
    const addNote = !g.note && edit.content.trim()
      ? ensureGroup(g).then(gid => {
        setOpenKey(gid);
        return axios.post(`${API}/study/keepsakes/note`, { token, content: edit.content, title: edit.title, received_date: edit.date,
          milestone_hours: edit.milestone ? hours : null, milestone_subject: subject, group_id: gid });
      })
      : Promise.resolve();
    Promise.all(jobs).then(() => addNote)
      .then(() => { closeGift(); return load(); })
      .catch(err => setErrorMsg(describeError(err)))
      .finally(() => setBusy(false));
  };

  const addMorePhotos = (files) => {
    const list = Array.from(files || []);
    if (!list.length || !open) return;
    const g = open;
    setBusy(true);
    setErrorMsg('');
    ensureGroup(g)
      .then(gid => {
        setOpenKey(gid);
        return uploadImages(list.map(file => ({ file, inRotation: false })), {
          title: g.title, date: g.received_date, milestone: g.milestone_hours, milestoneSubject: g.milestone_subject, groupId: gid,
        });
      })
      .then(load)
      .catch(err => { setErrorMsg(describeError(err)); load(); })
      .finally(() => { setBusy(false); setProgress(''); });
  };

  const setRotation = (k, on) => {
    setItems(list => list.map(x => (x.id === k.id ? { ...x, in_rotation: on } : x)));
    axios.put(`${API}/study/keepsakes/${k.id}`, { token, in_rotation: on })
      .catch(err => { setErrorMsg(describeError(err)); load(); });
  };

  const replaceImage = (file) => {
    const k = replaceTarget.current;
    if (!file || !k) return;
    setBusy(true);
    const data = new FormData();
    data.append('file', file);
    axios.post(`${API}/study/keepsakes/${k.id}/image?token=${encodeURIComponent(token)}`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(load)
      .catch(err => setErrorMsg(describeError(err)))
      .finally(() => setBusy(false));
  };

  const deletePiece = (k) => {
    setBusy(true);
    axios.delete(`${API}/study/keepsakes/${k.id}?token=${encodeURIComponent(token)}`)
      .then(() => { setConfirmPhoto(''); return load(); })
      .then(data => { if (open && open.items.length <= 1) closeGift(); return data; })
      .catch(err => setErrorMsg(describeError(err)))
      .finally(() => setBusy(false));
  };

  const deleteGift = () => {
    setBusy(true);
    Promise.all(open.items.map(k => axios.delete(`${API}/study/keepsakes/${k.id}?token=${encodeURIComponent(token)}`)))
      .then(() => { closeGift(); return load(); })
      .catch(err => setErrorMsg(describeError(err)))
      .finally(() => setBusy(false));
  };

  // ---- milestones (when things unlock) ----
  const saveMilestones = () => {
    const hours = milestoneDraft.split(/[,\s]+/).map(Number).filter(n => Number.isFinite(n) && n > 0);
    axios.put(`${API}/study/gift-milestones`, { token, hours })
      .then(() => { setShowMilestones(false); return load(); })
      .catch(err => setErrorMsg(describeError(err)));
  };

  const milestoneOptions = (
    <>
      <option value="">none</option>
      {milestones.map(m => (
        <option key={msValue(m.subject, m.hours)} value={msValue(m.subject, m.hours)}>{milestoneLabel(m.hours, m.subject)}{m.unlocked ? '' : ' (not yet)'}</option>
      ))}
    </>
  );

  // "10 hours" big, "of nursing" small
  const labelParts = (g) => {
    if (!g.milestone_hours) return [g.title || 'from Caleb', ''];
    return [`${g.milestone_hours} hours`, manySubjects ? `of ${subjectNames[g.milestone_subject || 'nursing'] || g.milestone_subject}` : 'together'];
  };

  // the next thing that unlocks, per subject
  const nextUp = [];
  const seenSub = {};
  milestones.forEach(m => { if (!m.unlocked && !seenSub[m.subject]) { seenSub[m.subject] = true; nextUp.push(m); } });

  // ---- looking at stickers, just the stickers ----
  const viewGift = viewer ? gifts.find(g => g.key === viewer.key) : null;
  const viewImages = viewGift ? viewGift.images : [];
  const viewIndex = viewer ? Math.min(viewer.i, Math.max(0, viewImages.length - 1)) : 0;
  const step = (d) => setViewer(v => (v ? { ...v, i: (v.i + d + viewImages.length) % viewImages.length } : v));
  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setViewer(null);
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewer, viewImages.length]);
  const touchX = useRef(null);

  // stickers stick on as each gift scrolls into view
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    observer.current = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { const k = e.target.getAttribute('data-gift'); setShown(x => (x[k] ? x : { ...x, [k]: true })); } });
    }, { threshold: 0.15 });
    document.querySelectorAll('[data-gift]').forEach(el => observer.current.observe(el));
    return () => observer.current && observer.current.disconnect();
  }, [items.length]);
  const noObserver = typeof IntersectionObserver === 'undefined';

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <style>{`
        .ks-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
        .ks-tile { transform: rotate(var(--r)); transition: transform .35s cubic-bezier(.2,1.6,.4,1); opacity: 0; }
        .ks-tile.in { animation: ksstick .55s cubic-bezier(.2,1.5,.4,1) both; animation-delay: var(--d); }
        .ks-tile:hover { transform: rotate(0deg) scale(1.07) translateY(-4px); z-index: 2; }
        .ks-tile:active { transform: rotate(0deg) scale(.96); }
        .ks-tile img { transition: filter .3s ease; }
        .ks-tile:hover img { filter: drop-shadow(0 14px 22px rgba(0,0,0,0.55)) drop-shadow(0 0 18px rgba(201,147,90,0.18)) !important; }
        @keyframes ksstick { 0% { opacity: 0; transform: rotate(calc(var(--r) * 3)) scale(1.25) translateY(-14px); } 60% { opacity: 1; } 100% { opacity: 1; transform: rotate(var(--r)) scale(1); } }
        .ks-seal { animation: ksseal 6s ease-in-out infinite; }
        @keyframes ksseal { 0%, 100% { transform: rotate(-8deg); } 50% { transform: rotate(-3deg); } }
        .ks-spark { position: absolute; color: #e2b77f; pointer-events: none; animation: kstw 2.8s ease-in-out infinite; }
        @keyframes kstw { 0%, 100% { opacity: 0; transform: scale(.5); } 50% { opacity: .9; transform: scale(1); } }
        .ks-view img { animation: ksfloat 5s ease-in-out infinite, ksfade .3s ease; }
        @keyframes ksfloat { 0%, 100% { transform: translateY(0) rotate(-1deg); } 50% { transform: translateY(-8px) rotate(1deg); } }
        .ks-waits { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 10px; }
        .ks-label { font-size: 30px; }
        @media (min-width: 760px) {
          .ks-grid { gap: 18px; }
          .ks-label { font-size: 38px; }
        }
        /* on a wide screen, his note sits beside the stickers so you see everything at once */
        @media (min-width: 1000px) {
          .ks-split { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); gap: 32px; align-items: start; }
          .ks-split .ks-letter { margin-top: 0 !important; }
        }
        .ks-open { animation: ksin .3s ease; }
        @keyframes ksin { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: none; } }
        @keyframes ksfade { from { opacity: 0; transform: scale(.97); } to { opacity: 1; transform: none; } }
        .ks-btn:focus-visible, .ks-tile:focus-visible { outline: 2px solid #c9935a; outline-offset: 3px; border-radius: 12px; }
        @media (prefers-reduced-motion: reduce) { .ks-tile, .ks-open, .ks-view img, .ks-seal, .ks-spark { transition: none; animation: none; opacity: 1; } }
      `}</style>

      <header style={s.header}>
        <button className="ks-btn" style={s.circle} onClick={() => navigate('/study')} aria-label="back">←</button>
        <div style={s.title}>From Caleb ♡</div>
        <button className="ks-btn" style={s.circle} onClick={() => startAdd()} title="add something he gave you" aria-label="add a gift">
          <PlusIcon />
        </button>
      </header>

      <main style={s.main}>
        {errorMsg && !open && !adding && <div style={s.error} onClick={() => setErrorMsg('')}>{errorMsg}</div>}

        {/* unlocked, but not added yet — a quiet line, never a placeholder gift */}
        {waiting.length > 0 && (
          <div className="ks-waits" style={{ marginBottom: '8px' }}>
            {waiting.map(w => (
              <button key={msValue(w.subject, w.hours)} className="ks-btn" style={s.waiting} onClick={() => startAdd({ milestone: msValue(w.subject, w.hours) })}>
                <span><HeartIcon /> something from Caleb is waiting · {milestoneLabel(w.hours, w.subject)}</span>
                <span style={s.waitingAdd}>add it</span>
              </button>
            ))}
          </div>
        )}
        {loaded && nextUp.length > 0 && (
          <div style={s.nextUp}>next from Caleb: {nextUp.map(m => milestoneLabel(m.hours, m.subject)).join(' · ')}</div>
        )}

        {loaded && items.length === 0 && waiting.length === 0 && (
          <div style={s.empty}>
            nothing in here yet.<br />
            when he gives you something, keep it here.
          </div>
        )}

        {/* every gift: what it was for, all his stickers, then his note */}
        <div style={s.gifts}>
          {gifts.map(g => {
            const [big, small] = labelParts(g);
            const long = g.note && (g.note.content || '').length > 420;
            return (
              <section key={g.key} data-gift={g.key} style={s.gift} aria-label={`${big} ${small}`.trim()}>
                <div style={s.giftHead}>
                  <div style={s.sealWrap} aria-hidden="true">
                    <div className="ks-seal" style={s.seal}>
                      <div style={s.sealRing}>
                        <span style={s.sealNum}>{g.milestone_hours ? `${g.milestone_hours}h` : '♡'}</span>
                      </div>
                    </div>
                    <span className="ks-spark" style={{ top: '-4px', right: '-6px', fontSize: '12px' }}>✦</span>
                    <span className="ks-spark" style={{ bottom: '2px', left: '-8px', fontSize: '9px', animationDelay: '1.3s' }}>✦</span>
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="ks-label" style={s.giftLabel}>
                      {big}{small && <span style={s.giftLabelSmall}> {small}</span>}
                    </div>
                    <div style={s.giftSub}>
                      {[g.milestone_hours && g.title ? g.title : '', longDate(g.received_date),
                        g.images.length ? `${g.images.length} ${g.images.length === 1 ? 'sticker' : 'stickers'}` : ''].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button className="ks-btn" style={s.editBtn} onClick={() => startEdit(g)} aria-label="edit this gift" title="edit"><PencilIcon /></button>
                </div>

                <div className={g.images.length && g.note ? 'ks-body ks-split' : 'ks-body'}>
                {g.images.length > 0 && (
                  <div className="ks-grid">
                    {g.images.map((k, i) => (
                      <button key={k.id} className={shown[g.key] || noObserver ? 'ks-tile in' : 'ks-tile'} style={{ ...s.tile, '--r': `${tiltOf(k.id)}deg`, '--d': `${i * 70}ms` }} onClick={() => setViewer({ key: g.key, i })} aria-label={`sticker ${i + 1} of ${g.images.length}`}>
                        <img src={k.image_url} alt="" style={s.tileImg} loading="lazy" />
                      </button>
                    ))}
                  </div>
                )}

                {g.note && (
                  <div className="ks-letter" style={{ ...s.letter, ...(g.images.length ? { marginTop: '18px' } : {}) }}>
                    <span style={s.tape} aria-hidden="true" />
                    <div style={{ ...s.letterText, ...(long && !expanded[g.key] ? s.clamp : {}) }}>{g.note.content}</div>
                    {long && (
                      <button style={s.readMore} onClick={() => setExpanded(x => ({ ...x, [g.key]: !x[g.key] }))}>
                        {expanded[g.key] ? 'fold it back up' : 'read the whole note'}
                      </button>
                    )}
                    {!signed(g.note.content) && (!long || expanded[g.key]) && <div style={s.signature}>— Caleb ♡</div>}
                  </div>
                )}
                </div>
              </section>
            );
          })}
        </div>

        {/* when things unlock */}
        {loaded && (
          <div style={s.milestoneBox}>
            {!showMilestones ? (
              <button style={s.quietLink} onClick={() => { setMilestoneDraft([...new Set(milestones.map(m => m.hours))].join(', ')); setShowMilestones(true); }}>
                when something unlocks
              </button>
            ) : (
              <div style={{ ...s.form, maxWidth: '520px', margin: '0 auto' }}>
                <div style={s.hint}>study-hour milestones, separated by commas — every subject counts its own hours toward them. what he gives you at each is up to him.</div>
                <input style={s.input} value={milestoneDraft} onChange={e => setMilestoneDraft(e.target.value)} inputMode="numeric" aria-label="milestone hours" />
                <div style={s.row}>
                  <button style={s.primary} onClick={saveMilestones}>save</button>
                  <button style={s.quiet} onClick={() => setShowMilestones(false)}>cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* LOOKING AT A STICKER — nothing but the sticker */}
      {viewGift && viewImages.length > 0 && (
        <div className="ks-view" style={s.viewer} onClick={() => setViewer(null)} role="dialog" aria-label="sticker"
          onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            if (touchX.current == null || viewImages.length < 2) return;
            const dx = e.changedTouches[0].clientX - touchX.current;
            touchX.current = null;
            if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
          }}>
          <button className="ks-btn" style={s.viewClose} onClick={() => setViewer(null)} aria-label="close"><CloseIcon /></button>
          <img key={viewImages[viewIndex].id} src={viewImages[viewIndex].image_url} alt="" style={s.viewImg} onClick={e => e.stopPropagation()} />
          {viewImages.length > 1 && (
            <>
              <button className="ks-btn" style={{ ...s.viewArrow, left: '12px' }} onClick={e => { e.stopPropagation(); step(-1); }} aria-label="previous sticker">‹</button>
              <button className="ks-btn" style={{ ...s.viewArrow, right: '12px' }} onClick={e => { e.stopPropagation(); step(1); }} aria-label="next sticker">›</button>
              <div style={s.viewDots} aria-hidden="true">
                {viewImages.map((k, i) => <span key={k.id} style={s.dot(i === viewIndex)} />)}
              </div>
            </>
          )}
        </div>
      )}

      {/* EDITING ONE GIFT */}
      {open && editing && edit && (
        <div style={s.overlay} onClick={closeGift}>
          <div className="ks-open" style={s.sheet} onClick={e => e.stopPropagation()} role="dialog" aria-label="edit this gift">
            <div style={s.sheetBar}>
              <div style={s.sheetTitle}>{labelParts(open).join(' ').trim()}</div>
              <button className="ks-btn" style={s.circle} onClick={closeGift} aria-label="close"><CloseIcon /></button>
            </div>
            <div style={s.form}>
              <div style={s.field}>stickers
                <span style={s.hint}>tap a sticker's chip to put it in (or take it out of) the stickers you see while you study.</span>
                <div style={s.thumbGrid}>
                  {open.images.map(k => (
                    <div key={k.id} style={s.thumbWrap}>
                      <img src={k.image_url} alt="" style={s.thumbImg} />
                      {confirmPhoto === k.id ? (
                        <div style={s.thumbConfirm}>
                          <button style={s.thumbYes} disabled={busy} onClick={() => deletePiece(k)}>remove</button>
                          <button style={s.thumbNo} onClick={() => setConfirmPhoto('')}>keep</button>
                        </div>
                      ) : (
                        <>
                          <button style={s.thumbX} onClick={() => setConfirmPhoto(k.id)} aria-label="remove this sticker">×</button>
                          <button style={s.thumbSwap} disabled={busy} onClick={() => { replaceTarget.current = k; replaceRef.current?.click(); }} aria-label="replace this sticker" title="replace">↻</button>
                          <button style={s.rotChip(!!k.in_rotation)} aria-pressed={!!k.in_rotation} onClick={() => setRotation(k, !k.in_rotation)}>
                            {k.in_rotation ? '✓ rotation' : '+ rotation'}
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  <button style={s.thumbAdd} disabled={busy} onClick={() => moreRef.current?.click()}>
                    <ImageIcon size={20} /><span>{busy && progress ? progress : 'add photos'}</span>
                  </button>
                </div>
                <input ref={moreRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => { addMorePhotos(e.target.files); e.target.value = ''; }} />
                <input ref={replaceRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { replaceImage(e.target.files?.[0]); e.target.value = ''; }} />
              </div>
              <label style={s.field}>his note {open.note ? '' : '(optional — add one)'}
                <textarea style={s.textarea} rows={open.note ? 8 : 4} value={edit.content} onChange={e => setEdit({ ...edit, content: e.target.value })} aria-label="the note" placeholder="paste his note here…" />
              </label>
              <label style={s.field}>title (optional)
                <input style={s.input} value={edit.title} onChange={e => setEdit({ ...edit, title: e.target.value })} />
              </label>
              <div style={s.twoCol}>
                <label style={s.field}>received
                  <input style={s.input} type="date" value={edit.date} onChange={e => setEdit({ ...edit, date: e.target.value })} />
                </label>
                <label style={s.field}>milestone
                  <select style={s.input} value={edit.milestone} onChange={e => setEdit({ ...edit, milestone: e.target.value })}>{milestoneOptions}</select>
                </label>
              </div>
              {errorMsg && <div style={s.error}>{errorMsg}</div>}
              <div style={s.row}>
                <button style={s.primary} disabled={busy} onClick={saveEdit}>{busy ? 'saving…' : 'save'}</button>
                <button style={s.quiet} onClick={closeGift}>done</button>
              </div>
              <div style={s.deleteZone}>
                {!confirmDelete ? (
                  <button style={s.quiet} onClick={() => setConfirmDelete(true)}>delete this gift</button>
                ) : (
                  <div style={s.row}>
                    <span style={s.hint}>delete it for good? its stickers also leave the study rotation. this can't be undone.</span>
                    <button style={s.danger} disabled={busy} onClick={deleteGift}>delete</button>
                    <button style={s.quiet} onClick={() => setConfirmDelete(false)}>keep it</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADDING ONE — his note and any number of stickers, together */}
      {adding && (
        <div style={s.overlay} onClick={() => !busy && setAdding(false)}>
          <div className="ks-open" style={s.sheet} onClick={e => e.stopPropagation()} role="dialog" aria-label="something from Caleb">
            <div style={s.sheetBar}>
              <div style={s.sheetTitle}>something from Caleb</div>
              <button className="ks-btn" style={s.circle} onClick={() => !busy && setAdding(false)} aria-label="close"><CloseIcon /></button>
            </div>

            <div style={s.form}>
              <label style={s.field}><span style={s.fieldHead}><EnvelopeIcon size={14} /> his note (optional)</span>
                <textarea style={s.textarea} rows={6} placeholder="paste his note here…" value={form.content}
                  onChange={e => setForm({ ...form, content: e.target.value })} aria-label="the note" />
              </label>

              <div style={s.field}><span style={s.fieldHead}><ImageIcon size={14} /> his stickers (optional — choose as many as you like)</span>
                <div style={s.thumbGrid}>
                  {(form.photos || []).map((ph, i) => (
                    <div key={ph.preview} style={s.thumbWrap}>
                      <img src={ph.preview} alt="" style={s.thumbImg} />
                      <button style={s.thumbX} onClick={() => setForm(f => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))} aria-label="take this one out">×</button>
                      <button style={s.rotChip(ph.inRotation)} aria-pressed={ph.inRotation}
                        onClick={() => setForm(f => ({ ...f, photos: f.photos.map((p, j) => (j === i ? { ...p, inRotation: !p.inRotation } : p)) }))}>
                        {ph.inRotation ? '✓ rotation' : '+ rotation'}
                      </button>
                    </div>
                  ))}
                  <button style={s.thumbAdd} onClick={() => fileRef.current?.click()}>
                    <ImageIcon size={20} /><span>{(form.photos || []).length ? 'add more' : 'choose photos'}</span>
                  </button>
                </div>
                <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => { pickFiles(e.target.files); e.target.value = ''; }} />
                {(form.photos || []).length > 0 && (
                  <label style={{ ...s.toggleRow, justifyContent: 'flex-start', marginTop: '8px' }}>
                    <input type="checkbox" checked={!!form.allRotation} onChange={e => setAllRotation(e.target.checked)} style={{ accentColor: '#c9935a' }} />
                    put all of them in the study sticker rotation
                  </label>
                )}
              </div>

              <label style={s.field}>title (optional)
                <input style={s.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              </label>
              <div style={s.twoCol}>
                <label style={s.field}>received
                  <input style={s.input} type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
                </label>
                <label style={s.field}>milestone
                  <select style={s.input} value={form.milestone} onChange={e => setForm({ ...form, milestone: e.target.value })}>{milestoneOptions}</select>
                </label>
              </div>
              {errorMsg && <div style={s.error}>{errorMsg}</div>}
              <div style={s.row}>
                <button style={canSave && !busy ? s.primary : s.primaryOff} disabled={!canSave || busy} onClick={saveNew}>
                  {busy ? (progress || 'keeping it…') : 'keep it'}
                </button>
                <button style={s.quiet} onClick={() => !busy && setAdding(false)}>cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const paper = '#231b21';
const serif = "'Cormorant Garamond', serif";
const sans = "'DM Sans', sans-serif";
const s = {
  // its own scrolling area, like your other pages (the site stops the page itself from scrolling)
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: 'radial-gradient(ellipse 80% 50% at 50% 0%, #1a1319 0%, #0e0b0f 70%)', color: '#e8ddd0', fontFamily: sans, fontWeight: 300 },
  header: {
    position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px',
    background: 'linear-gradient(180deg, #120f18 0%, rgba(14,11,15,0.96) 100%)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(201,147,90,0.06)',
  },
  circle: {
    width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  },
  title: { flex: 1, fontFamily: serif, fontSize: '23px', fontStyle: 'italic' },
  // wide: uses the whole screen on a laptop, comfortable margins on a phone
  main: { maxWidth: '1120px', margin: '0 auto', padding: '22px 20px 90px', boxSizing: 'border-box' },
  waiting: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
    background: 'rgba(201,147,90,0.06)', border: '1px dashed rgba(201,147,90,0.25)', borderRadius: '16px', padding: '12px 16px',
    color: '#e8ddd0', fontFamily: sans, fontWeight: 300, fontSize: '13.5px', lineHeight: 1.5, cursor: 'pointer', textAlign: 'left',
  },
  waitingAdd: { fontFamily: sans, fontWeight: 400, fontSize: '12px', color: '#c9935a', whiteSpace: 'nowrap' },
  nextUp: { fontSize: '12px', color: '#6d5d4e', margin: '6px 2px 0' },
  empty: { textAlign: 'center', color: '#6d5d4e', fontFamily: serif, fontStyle: 'italic', fontSize: '18px', lineHeight: 1.7, padding: '60px 10px' },
  gifts: { display: 'flex', flexDirection: 'column', marginTop: '18px' },
  gift: { padding: '26px 0 34px', borderTop: '1px solid rgba(201,147,90,0.08)' },
  giftHead: { display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '22px' },
  sealWrap: { position: 'relative', flexShrink: 0, marginTop: '2px' },
  seal: {
    width: '58px', height: '58px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'radial-gradient(circle at 35% 30%, #e2b77f 0%, #c9935a 45%, #8a5f35 100%)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.5), inset 0 -3px 6px rgba(0,0,0,0.25), inset 0 2px 4px rgba(255,235,200,0.35)', transform: 'rotate(-8deg)',
  },
  sealRing: { width: '46px', height: '46px', borderRadius: '50%', border: '1px dashed rgba(14,11,15,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sealNum: { fontFamily: serif, fontStyle: 'italic', fontWeight: 400, fontSize: '19px', color: '#2a1c12', letterSpacing: '-0.02em' },
  giftLabel: { fontFamily: serif, fontStyle: 'italic', fontWeight: 300, color: '#e8ddd0', lineHeight: 1.1 },
  giftLabelSmall: { fontSize: '0.62em', color: '#c9935a' },
  giftSub: { fontSize: '12px', color: '#7a6a5a', marginTop: '6px' },
  editBtn: {
    width: '34px', height: '34px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, marginTop: '4px',
  },
  // no box: the sticker sits right on the page
  tile: {
    aspectRatio: '1 / 1', background: 'transparent', border: 'none', padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-in', minWidth: 0,
  },
  tileImg: { width: '100%', height: '100%', objectFit: 'contain', display: 'block', filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.45))' },
  letter: {
    background: paper, maxWidth: '760px', position: 'relative',
    border: '1px solid rgba(201,147,90,0.12)', borderRadius: '8px', padding: '24px 24px 22px', boxShadow: '0 14px 30px rgba(0,0,0,0.35)',
  },
  letterText: {
    fontFamily: sans, fontWeight: 300, fontSize: '15.5px', lineHeight: '28px', color: '#ece0d2', whiteSpace: 'pre-wrap',
    // ruled like letter paper — one rule under each line of writing
    background: 'repeating-linear-gradient(180deg, transparent 0px, transparent 27px, rgba(201,147,90,0.09) 27px, rgba(201,147,90,0.09) 28px)',
  },
  tape: {
    position: 'absolute', top: '-10px', left: '50%', width: '92px', height: '22px', transform: 'translateX(-50%) rotate(-2.5deg)',
    background: 'rgba(201,160,176,0.26)', borderRadius: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  signature: { textAlign: 'right', fontFamily: serif, fontStyle: 'italic', fontSize: '20px', color: '#c9935a', marginTop: '10px' },
  clamp: { display: '-webkit-box', WebkitLineClamp: 9, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  readMore: { marginTop: '10px', background: 'none', border: 'none', padding: 0, color: '#c9935a', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans },
  milestoneBox: { marginTop: '40px', textAlign: 'center' },
  quietLink: { background: 'none', border: 'none', color: '#6d5d4e', fontSize: '12px', cursor: 'pointer', fontFamily: sans, textDecoration: 'underline', textUnderlineOffset: '3px' },
  // the sticker viewer: dark, the sticker, nothing else
  viewer: { position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(8,6,9,0.94)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '56px 16px' , boxSizing: 'border-box' },
  viewImg: { maxWidth: 'min(92vw, 900px)', maxHeight: '82vh', objectFit: 'contain', display: 'block' },
  viewClose: {
    position: 'absolute', top: '16px', right: '16px', width: '40px', height: '40px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.2)',
    background: 'rgba(14,11,15,0.6)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  },
  viewArrow: {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: '42px', height: '42px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.2)', background: 'rgba(14,11,15,0.6)', color: '#c9935a', fontSize: '26px', lineHeight: 1,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 0 3px',
  },
  viewDots: { position: 'absolute', bottom: '22px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '6px' },
  dot: (on) => ({ width: on ? '16px' : '6px', height: '6px', borderRadius: '3px', background: on ? '#c9935a' : 'rgba(201,147,90,0.3)', transition: 'width .2s ease' }),
  overlay: { position: 'fixed', inset: 0, background: 'rgba(8,6,9,0.82)', backdropFilter: 'blur(6px)', zIndex: 100, overflowY: 'auto', padding: '24px 16px', boxSizing: 'border-box' },
  sheet: { maxWidth: '620px', margin: '0 auto', background: '#17121a', border: '1px solid rgba(201,147,90,0.12)', borderRadius: '22px', padding: '16px 18px 22px', boxSizing: 'border-box' },
  sheetBar: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', marginBottom: '14px' },
  sheetTitle: { flex: 1, fontFamily: serif, fontStyle: 'italic', fontSize: '20px' },
  toggleRow: { display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', fontSize: '12.5px', color: '#a8927f', marginTop: '14px', cursor: 'pointer' },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  field: { display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '11px', color: '#7a6a5a', letterSpacing: '0.04em' },
  fieldHead: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' },
  input: { width: '100%', boxSizing: 'border-box', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '10px', padding: '10px 12px', color: '#e8ddd0', fontSize: '14px', fontFamily: sans, outline: 'none', colorScheme: 'dark' },
  textarea: { width: '100%', boxSizing: 'border-box', background: paper, border: '1px solid rgba(201,147,90,0.15)', borderRadius: '10px', padding: '14px 16px', color: '#ece0d2', fontSize: '15px', lineHeight: 1.7, fontFamily: sans, fontWeight: 300, outline: 'none', resize: 'vertical' },
  row: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  primary: { padding: '10px 20px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '13.5px', cursor: 'pointer', fontFamily: sans },
  primaryOff: { padding: '10px 20px', borderRadius: '12px', border: 'none', background: 'rgba(201,147,90,0.15)', color: 'rgba(201,147,90,0.45)', fontSize: '13.5px', fontFamily: sans },
  quiet: { padding: '8px 10px', border: 'none', background: 'transparent', color: '#7a6a5a', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans },
  danger: { padding: '8px 16px', borderRadius: '12px', border: 'none', background: '#9c4545', color: '#f4e6e6', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans },
  deleteZone: { marginTop: '10px', paddingTop: '12px', borderTop: '1px solid rgba(201,147,90,0.08)' },
  hint: { fontSize: '12px', color: '#7a6a5a', lineHeight: 1.6, letterSpacing: 0 },
  thumbGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: '8px', marginTop: '4px' },
  thumbWrap: { position: 'relative', aspectRatio: '1 / 1', background: '#0e0b0f', borderRadius: '10px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  thumbImg: { maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' },
  thumbX: { position: 'absolute', top: '4px', right: '4px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', background: 'rgba(14,11,15,0.82)', color: '#e8ddd0', fontSize: '14px', lineHeight: 1, cursor: 'pointer', padding: 0, zIndex: 2 },
  thumbSwap: { position: 'absolute', top: '4px', left: '4px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', background: 'rgba(14,11,15,0.82)', color: '#c9a57a', fontSize: '13px', lineHeight: 1, cursor: 'pointer', padding: 0, zIndex: 2 },
  thumbConfirm: { position: 'absolute', inset: 0, background: 'rgba(14,11,15,0.86)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  thumbYes: { border: 'none', background: '#9c4545', color: '#f4e6e6', borderRadius: '9px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', fontFamily: sans },
  thumbNo: { border: 'none', background: 'transparent', color: '#a8927f', fontSize: '11px', cursor: 'pointer', fontFamily: sans },
  thumbAdd: { aspectRatio: '1 / 1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', borderRadius: '10px', border: '1px dashed rgba(201,147,90,0.3)', background: 'rgba(201,147,90,0.04)', color: '#a8927f', fontSize: '11.5px', cursor: 'pointer', fontFamily: sans, padding: '6px', textAlign: 'center' },
  rotChip: (on) => ({ position: 'absolute', bottom: '4px', left: '50%', transform: 'translateX(-50%)', border: on ? 'none' : '1px solid rgba(201,147,90,0.45)', zIndex: 2, whiteSpace: 'nowrap', background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'rgba(14,11,15,0.82)', color: on ? '#0e0b0f' : '#c9a57a', fontSize: '10.5px', borderRadius: '8px', padding: '3px 8px', cursor: 'pointer', fontFamily: sans }),
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0', cursor: 'pointer', marginBottom: '12px' },
};
